import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import anime from 'animejs';
import { FaShip, FaTools, FaUserCog, FaChevronRight, FaAnchor, FaGlobe, FaCompass } from 'react-icons/fa';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

import cargoship1 from '../assets/images/ghat1.webp';
import cargoship2 from '../assets/images/ship.webp';
import cargoship3 from '../assets/images/ghat.webp';

import NavigateButton from '../pages/Navigatebutton';
import MarineTrafficMaps from '../pages/MarineTrafficMaps';

const Home = () => {
  const navigate = useNavigate();
  
  const slides = [
    { img: cargoship1, alt: "Armar Shipping 1" },
    { img: cargoship2, alt: "Armar Shipping 2" },
    { img: cargoship3, alt: "Armar Shipping 3" }
  ];

  useEffect(() => {
    const observerOptions = {
      threshold: 0.05 
    };

    const handleIntersect = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const tl = anime.timeline({
            easing: 'easeOutExpo',
          });

          tl.add({
            targets: entry.target.querySelectorAll('.header-reveal'),
            opacity: [0, 1],
            translateY: [35, 0],
            duration: 1000,
          })
          .add({
            targets: entry.target.querySelectorAll('.glass-card'),
            opacity: [0, 1],
            scale: [0.99, 1], 
            translateY: [20, 0],
            delay: anime.stagger(80, { start: 100 }), 
            duration: 750
          }, '-=900');

          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    document.querySelectorAll('.scroll-section').forEach(section => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main className="w-full min-h-screen bg-[#fdfcfb] selection:bg-[#c4a47c] selection:text-white overflow-x-hidden relative">
      
      <h1 className="hidden">Armar Shipping Company</h1>

      <div className="fixed top-0 left-0 w-full h-full pointer-events-none z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[300px] md:w-[800px] h-[300px] md:h-[800px] bg-[#0a111f]/[0.02] rounded-full blur-[80px] md:blur-[120px]"></div>
        <div className="absolute bottom-[10%] left-[-5%] w-[250px] md:w-[600px] h-[250px] md:h-[600px] bg-[#c4a47c]/[0.05] rounded-full blur-[80px] md:blur-[100px]"></div>
      </div>

      {/* Swiper Slider Section */}
      <section className="relative w-full h-auto md:h-[750px] overflow-hidden bg-white shadow-xl z-10 border-b border-[#0a111f]/5">
        <style>
          {`
            .swiper-pagination-bullet { background: #0a111f !important; opacity: 0.2 !important; }
            .swiper-pagination-bullet-active { background: #c4a47c !important; opacity: 1 !important; width: 25px !important; border-radius: 4px !important; }
          `}
        </style>
        <Swiper
          modules={[Autoplay, EffectFade, Pagination]}
          effect="fade"
          autoHeight={true} 
          speed={1200} 
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          className="w-full h-full"
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={index} className="w-full h-full relative group">
              <img
                src={slide.img}
                alt={slide.alt}
                className="w-full h-auto md:h-full md:object-contain object-center block bg-[#fdfcfb]"
                loading={index === 0 ? "eager" : "lazy"}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a111f]/20 via-transparent to-transparent pointer-events-none" />
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* About Section */}
      <section className="scroll-section py-24 w-full bg-[#f4f1ea] relative z-10 border-y border-[#0a111f]/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            
            <div className="w-full lg:w-1/2 header-reveal">
              <span className="text-[11px] font-bold uppercase tracking-[1em] text-[#c4a47c] mb-4 block">Since 2011</span>
              <h2 className="text-4xl md:text-6xl font-black text-[#0a111f] uppercase italic tracking-tighter leading-[0.85] mb-6">
                Armar <span className="text-[#c4a47c]">Shipping Company</span>
              </h2>
              <div className="w-16 h-1 bg-[#c4a47c]/30"></div>
            </div>

            <div className="w-full lg:w-1/2 glass-card">
              <div className="bg-[#0a111f] text-white p-12 md:p-16 rounded-[4rem] shadow-[0_50px_100px_-20px_rgba(10,17,31,0.3)] relative overflow-hidden group">
                <FaAnchor className="absolute -bottom-10 -right-10 text-white/5 text-[15rem] rotate-12" />
                <p className="text-base md:text-lg font-light italic leading-relaxed relative z-10 border-l-4 border-[#c4a47c] pl-8">
                  ARMAR SHIPPING COMPANY (ARMARSHIP), has been established in Istanbul on 11.11.2011 with the aim of providing a comprehensive agency services for valuable Charterers & Ship Owners & Operators in a proper manner, reliable and professional way at TURKISH STRAITS and PORTS plus at Turkish main Shipyards in Tuzla and Yalova areas.
                </p>
                <button 
                  onClick={() => handleNavigation('/about')}
                  className="mt-12 flex items-center gap-4 text-xs font-bold uppercase tracking-[0.5em] hover:text-[#c4a47c] transition-all relative z-10"
                >
                  Learn More <FaChevronRight size={10} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="scroll-section py-24 px-6 lg:px-20 relative z-10">
        <div className="text-center mb-20 header-reveal">
          <h2 className="text-3xl md:text-5xl font-black text-[#0a111f] uppercase italic tracking-tighter leading-none mb-6">
            Our <span className="text-[#c4a47c]">Services</span>
          </h2>
          <div className="w-20 h-1 bg-[#c4a47c] mx-auto mt-4"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {[
            { icon: <FaShip />, desc: "Professional Agency Services at Turkish Straits and all Ports" },
            { icon: <FaTools />, desc: "All kinds of repairs & agency services at Tuzla & Yalova", dark: true },
            { icon: <FaUserCog />, desc: "Fresh Provisions, Deck and Engine Stores, Technical materials, repairs" }
          ].map((service, idx) => (
            <div 
              key={idx} 
              className={`glass-card group p-12 rounded-[3rem] transition-all duration-700 border flex flex-col items-center text-center ${
                service.dark 
                ? 'bg-[#0a111f] border-[#0a111f] text-white shadow-2xl scale-105 z-20' 
                : 'bg-white/40 backdrop-blur-xl border-white/60 text-[#0a111f] shadow-xl hover:bg-white/80'
              }`}
            >
              <div className={`w-20 h-20 rounded-2xl flex items-center justify-center text-4xl mb-8 transition-transform group-hover:rotate-12 ${
                service.dark ? 'bg-[#c4a47c] text-[#0a111f]' : 'bg-[#0a111f] text-[#c4a47c]'
              }`}>
                {service.icon}
              </div>
              <p className="text-base md:text-lg font-light leading-relaxed opacity-80">{service.desc}</p>
              <div className={`mt-8 w-10 h-[2px] transition-all duration-500 group-hover:w-24 ${service.dark ? 'bg-[#c4a47c]' : 'bg-[#0a111f]'}`}></div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="scroll-section px-6 lg:px-20 pb-32">
        <div 
          onClick={() => handleNavigation('/services')}
          className="max-w-7xl mx-auto glass-card cursor-pointer relative group overflow-hidden rounded-[3rem] p-1 shadow-xl bg-gradient-to-r from-[#0a111f] to-[#c4a47c]"
        >
          <div className="bg-[#fdfcfb]/90 backdrop-blur-2xl rounded-[2.9rem] p-10 flex items-center justify-between transition-all group-hover:bg-white/40">
            <div className="flex items-center gap-8">
              <div className="w-16 h-16 rounded-full bg-[#0a111f] flex items-center justify-center text-[#c4a47c] text-2xl group-hover:scale-110 transition-transform">
                <FaCompass />
              </div>
              <h5 className="text-lg md:text-2xl font-black italic text-[#0a111f] tracking-tighter">Explore All Services</h5>
            </div>
            <div className="w-14 h-14 rounded-full border border-[#0a111f]/10 flex items-center justify-center group-hover:bg-[#0a111f] group-hover:text-white transition-all">
              <FaChevronRight />
            </div>
          </div>
        </div>
      </section>

      <MarineTrafficMaps /> 
      <NavigateButton />

      <div className="h-2 bg-gradient-to-r from-[#0a111f] via-[#c4a47c] to-[#0a111f]"></div>
    </main>
  );
};

export default Home;